import { get } from "lodash";
import axiosFactory from "../core/common/axiosFactory";
import ConfirmedFailedPaymentDto from "./dtos/ConfirmFailedPaymentResponseDto";
import ConfirmSuccessPaymentResponseDto from "./dtos/ConfirmSuccessPaymentResponseDto";
import CreatePaymentRequestDto from "./dtos/CreatePaymentRequestDto";
import CreatePaymentResponseDto from "./dtos/CreatePaymentResponseDto";
import PaymentRequestDto from "./dtos/PaymentRequestDto";
import UpdatePaymentRequestDto from "./dtos/UpdatePaymentRequestDto";
import UpdatePaymentResponseDto from "./dtos/UpdatePaymentResponseDto";
import DisabledPaymentRequestError from "./errors";

const axios = axiosFactory.createAxiosInstance("PaymentContainerClient");

export default class PaymentContainerClient {
    async getPaymentRequest(
        paymentRequestToken: string
    ): Promise<PaymentRequestDto> {
        try {
            const response = await axios.request<PaymentRequestDto>({
                method: "GET",
                url: `api/v1/public/paymentRequest/${paymentRequestToken}`,
            });
            return response.data;
        } catch (error) {
            if (get(error, "response.status") === 410) {
                throw new DisabledPaymentRequestError();
            }

            throw error;
        }
    }

    async createPayment(
        paymentRequestToken: string,
        createPaymentRequestDto: CreatePaymentRequestDto
    ): Promise<CreatePaymentResponseDto> {
        const response = await axios.request<CreatePaymentResponseDto>({
            method: "POST",
            url: `api/v1/public/paymentRequest/${paymentRequestToken}/pay`,
            data: createPaymentRequestDto,
        });

        return response.data;
    }

    async updatePayment(
        paymentRequestToken: string,
        updatePaymentRequestDto: UpdatePaymentRequestDto,
        paymentId: string
    ): Promise<UpdatePaymentResponseDto> {
        const response = await axios.request<UpdatePaymentResponseDto>({
            method: "PATCH",
            url: `api/v1/public/paymentRequest/${paymentRequestToken}/payment/${paymentId}`,
            data: updatePaymentRequestDto,
        });

        return response.data;
    }

    async setPaymentRequestAsPaid(
        paymentRequestToken: string,
        note?: string
    ): Promise<void> {
        await axios.request({
            method: "POST",
            url: `api/v1/public/paymentRequest/${paymentRequestToken}/paid`,
            data: { note: note },
        });
    }

    async confirmPayment(
        paymentRequestToken: string,
        jwtToken: string
    ): Promise<ConfirmSuccessPaymentResponseDto | ConfirmedFailedPaymentDto> {
        const response = await axios.request<
            ConfirmSuccessPaymentResponseDto | ConfirmedFailedPaymentDto
        >({
            method: "POST",
            url: `api/v1/public/paymentRequest/${paymentRequestToken}/confirm`,
            data: { license: jwtToken },
        });

        return response.data;
    }

    async downloadDocument(
        paymentRequestToken: string,
        documentId: string
    ): Promise<Blob> {
        const response = await axios.request<Blob>({
            method: "GET",
            url: `api/v1/public/paymentRequest/${paymentRequestToken}/documents/${documentId}/download`,
            responseType: "blob",
        });

        return response.data;
    }

    async getDocumentPreview(
        paymentRequestToken: string,
        documentId: string
    ): Promise<string> {
        const response = await axios.request<string>({
            method: "GET",
            url: `api/v1/public/paymentRequest/${paymentRequestToken}/documents/${documentId}/preview`,
        });

        return response.data;
    }
}
